/**
 * Who a task on a given project can be handed to.
 *
 * **The project decides, not the operator's address book.** A task belongs to a
 * project, and assigning it to somebody with no access to that project produces
 * work they cannot open. So the list is built from the project's own
 * `partners[]` ACL rather than from every partner the operator happens to have.
 *
 * That also means the list cannot exist until a project is chosen, which is why
 * this returns an empty array for no project rather than falling back to
 * something broader.
 *
 * Shared by the add form and the edit sheet, which need the same answer for the
 * same reason. Two copies would drift, and the drift would show as a name
 * offered in one place and missing in the other, on the same task.
 */

export interface TaskAssignee {
	id: string
	name: string
}

/** Ids arrive populated or bare depending on which endpoint returned them. */
export const assigneeIdOf = (value: any): string =>
	value == null ? '' : typeof value === 'object' ? String(value._id ?? '') : String(value)

const nameOf = (person: any): string =>
	person?.name || person?.firstName || person?.email || 'Someone'

export interface TaskAssigneeArgs {
	/** The project the task belongs to, as `state.projects` holds it. */
	project?: any
	userId?: string
	userName?: string
	/**
	 * Whoever the task is assigned to now, if it already exists. Kept even when
	 * they are no longer on the project.
	 */
	current?: any
}

/**
 * The operator, the project's owner, then everyone granted access to it.
 *
 * **The owner is included** even though they are not in `partners[]`. They are
 * definitionally on the project, and leaving them out would mean a partner
 * could never hand a task back to the person whose project it is.
 *
 * **A partner row is only usable if `partnerUserId` was populated.** It arrives
 * as a bare id from some endpoints, and a bare id has no name to show, so those
 * rows are dropped rather than rendered as "Someone".
 *
 * **The current assignee is kept regardless.** They may have been removed from
 * the project since. Dropping them from the options would silently reassign the
 * task the next time somebody opened it and saved.
 */
export const taskAssignees = ({
	project,
	userId,
	userName,
	current,
}: TaskAssigneeArgs): TaskAssignee[] => {
	const me: TaskAssignee | null = userId
		? {
				id: String(userId),
				// "(me)" rather than the bare name: in a list of colleagues the
				// operator needs to find themselves at a glance.
				name: userName ? `${userName} (me)` : 'Me',
			}
		: null

	const currentId = assigneeIdOf(current)

	// No project, no list. The question "who can this be given to" has no
	// answer until the task has somewhere to live.
	if (!project) {
		const only: TaskAssignee[] = []
		if (currentId) only.push({ id: currentId, name: nameOf(current) })
		return only
	}

	const seen = new Set<string>()
	const list: TaskAssignee[] = []

	const add = (id: string, name: string) => {
		if (!id || seen.has(id)) return
		seen.add(id)
		list.push({ id, name })
	}

	if (me) add(me.id, me.name)

	const ownerId = assigneeIdOf(project.owner)
	if (ownerId) add(ownerId, nameOf(project.owner))

	for (const row of project.partners || []) {
		const person = row?.partnerUserId
		// A bare id carries no name, so it would render as "Someone" and tell
		// the user nothing. Better absent than meaningless.
		if (person && typeof person === 'object') add(assigneeIdOf(person), nameOf(person))
	}

	if (currentId) add(currentId, nameOf(current))

	return list
}

export default taskAssignees
